import { useContext, useState } from "react"
import { Alert, EventSessionForm, LineLoader } from "@components"
import { UserContext, NotificationContext } from "@context"
import { EXTEND_SESSION_MUTATION } from "@graphql"
import { getGraphQLErrorMessage, mySqlToLocal } from "@helpers"
import { useSessionBookingCounts, useUpdateSession } from "@hooks"
import { useApolloClient, useMutation } from "@apollo/client";
import { Button } from "@heroui/react"
import dayjs from "dayjs"
import moment from "moment"
import PropTypes from "prop-types"
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import ExtendSession from "./ExtendSession"

dayjs.extend(isSameOrAfter);

const EditSession = ({ handleCancel, event, session }) => {
  const [isDirty, setIsDirty] = useState(false)
  const [errors, setErrors] = useState({})
  const [dirtyFields, setDirtyFields] = useState({})
  const [showExtendSession, setShowExtendSession] = useState(false)

  /**
   * Context
   */
  const { notify } = useContext(NotificationContext)

  /**
   * Hooks
   */
  const client = useApolloClient();
  const { setCurrentEvent } = useContext(UserContext)

  const { data: bookingCountData, error: bookingCountError, loading: loadingBookingCount } = useSessionBookingCounts({
    variables: { id: session.id },
    fetchPolicy: 'network-only'
  })
  
  const {updateSession, loading: saving} = useUpdateSession()

  const [extendSession, { loading: isExtendingSession }] = useMutation(EXTEND_SESSION_MUTATION, {
    onCompleted: (response) => {
      notify({ title: "Success", type: "success", content: `Session extended successfully.`, open: true })
      setShowExtendSession(false)
      client.refetchQueries({ include: "active" }); // refetch all active queries to reflect the updated session
    },

    onError: (error) => {
      notify({ title: "Error", type: "danger", content: getGraphQLErrorMessage(error, `An error occurred while extending the session.`), open: true })
    }
  })
    
  /**
   * Functions
   */
  const handleDirtyChange = (isDirty, dirtyFields) => {
    setIsDirty(isDirty)
    setDirtyFields(dirtyFields)
  }
  
  const handleErrorChange = (errors) => setErrors(errors)

  const handleExtendSession = (startTime, endTime, noOfSlots) => {
    const currentStartTime = mySqlToLocal(session.starts_at)
    const currentEndTime = mySqlToLocal(session.ends_at)

    extendSession({
      variables: {
        input: {
          session_id: session.id,
          starts_at: currentStartTime.format("YYYY-MM-DD") + " " + startTime + ":00",
          ends_at: currentEndTime.format("YYYY-MM-DD") + " " + endTime + ":00",
          no_of_slots: noOfSlots
        }
      }
    })
  }
  
  const handleSave = (data) => {
    updateSession({
      variables: {
        input: {
          id: session.id,
          starts_at: dayjs(data.start_date).format("YYYY-MM-DD") + " " + data.start_time.format("HH:mm:ss"),
          ends_at: dayjs(data.start_date).format("YYYY-MM-DD") + " " + data.end_time.format("HH:mm:ss"),
          slot_duration: parseInt(data.slot_duration, 10),
          slot_gap: parseInt(data.slot_gap, 10),
          title: data.title,
        },
      },
      onCompleted: (response) => {
        setCurrentEvent(event.id) // forces user context to refresh event data
        notify({ title: "Success", type: "success", content: `Session updated successfully.`, open: true })
        client.refetchQueries({ include: "active" }); // refetch all active queries to reflect the new session
        handleCancel()
      },

      onError: (error) => {
        notify({ title: "Error", type: "danger", content: getGraphQLErrorMessage(error, `An error occurred while updating the session.`), open: true })
      }
    })
  }

  const handleShowExtendSession = () => setShowExtendSession(true)
  
  const hasErrors = () => Object.keys(errors).length > 0

  const formattedSession = {
    ...session,
    start_date: session?.starts_at ? mySqlToLocal(session?.starts_at).startOf('day').toDate() : null,
    start_time: moment(mySqlToLocal(session?.starts_at).format('HH:mm:ss'), "HH:mm:ss"),
    end_time: moment(mySqlToLocal(session?.ends_at).format('HH:mm:ss'), "HH:mm:ss")
  }

  if (loadingBookingCount) {
    return <LineLoader message="Please wait loading..." classNames="mt-8" />
  }
  
  const bookingCount = bookingCountData?.session?.no_of_bookings || 0
  const hasBookings = bookingCount > 0
  const isSessionLiveOrExpired = dayjs().isSameOrAfter(dayjs.utc(session.starts_at, "YYYY-MM-DD HH:mm:ss"))
  const isReadOnly = hasBookings || bookingCountError || isSessionLiveOrExpired
  const willCauseSlotRecalculation = isDirty && ['start_date','start_time','end_time','slot_duration','slot_gap'].some(k => k in dirtyFields);
  const isSaveButtonDisabled = !isDirty || isReadOnly || hasErrors()
  
  if (showExtendSession) {
    return <ExtendSession 
      session={session} 
      handleCancel={() => setShowExtendSession(false)} 
      handleSave={(startTime, endTime, noOfSlots) => handleExtendSession(startTime, endTime, noOfSlots)}
      isSaving={isExtendingSession}
    />
  }

  return (
    <div>
      {isSessionLiveOrExpired && (
        <Alert
          classNames="mb-4"
          type="info"
          title="Read Only Mode"
          description="Cannot edit a session when it's live or expired."
        />
      )}
      
      {hasBookings && !isSessionLiveOrExpired && (
        <Alert 
          classNames="mb-4" 
          type="info" 
          title={`Read Only Mode - ${bookingCount} booking(s) found`} 
          description={`To edit delete all bookings for this session.`} 
        />
      )}

      {bookingCountError && !isSessionLiveOrExpired && (
        <Alert 
          classNames="mb-4" 
          type="info" 
          title={`Read Only Mode`} 
          description={`Unable to verify if bookings exist for this session.`} 
        />
      )}

      {willCauseSlotRecalculation && !hasBookings && !bookingCountError && (
        <Alert 
          classNames="mb-4"
          type="danger" 
          title="Slot Recalculation Warning" 
          description="All slots will be reset to availble!" 
        />
      )}
      
      <EventSessionForm
        handleShowExtendSession={handleShowExtendSession}
        handleSubmit={(data) => handleSave(data)}
        session={formattedSession}
        onDirtyChange={(isDirty, dirtyFields) => handleDirtyChange(isDirty, dirtyFields)}
        onErrorChange={(errors) => handleErrorChange(errors)}
        isReadOnly={isReadOnly}
        showExtendSessionButton={!isSessionLiveOrExpired && hasBookings}
      >
        <div className="flex flex-col gap-2 mt-6">
          <Button color="default" fullWidth onPress={handleCancel}>Cancel</Button>
          <Button className="font-semibold" type="submit" color="secondary" fullWidth isDisabled={isSaveButtonDisabled} isLoading={saving}>Save</Button>
        </div>
      </EventSessionForm>
    </div>
  )
}

EditSession.propTypes = {
  event: PropTypes.object.isRequired,
  handleCancel: PropTypes.func.isRequired,
  session: PropTypes.object.isRequired
}

export default EditSession